import type { Aresta, DefinicaoFluxo, No } from '@mymaxx/flow-engine';
import { ROTULO_GATILHO, type FluxoDetalhe, type VersaoFluxo } from '@mymaxx/shared';
import { Aviso, Botao, Modal } from '@mymaxx/ui';
import {
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  addEdge,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type NodeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Check, History, Play, Save, Upload } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { CATALOGO_NOS, NOS_DA_PALETA, noPadrao, type TipoNo } from '../fluxos/catalogo-nos';
import { ArestaFluxo } from '../fluxos/ArestaFluxo';
import { NoFluxo, type NoCanvas } from '../fluxos/NoFluxo';
import { PropriedadesAresta } from '../fluxos/PropriedadesAresta';
import { PropriedadesNo } from '../fluxos/PropriedadesNo';
import { Simulador } from '../fluxos/Simulador';
import { ErroApi } from '../lib/api';
import { fluxosApi } from '../lib/fluxos-api';
import { gestaoApi } from '../lib/gestao-api';
import { useSessao } from '../lib/sessao';

const TIPOS_DE_NO = { mymaxx: NoFluxo };
const TIPOS_DE_ARESTA = { mymaxx: ArestaFluxo };

/** Converte a definição salva para o formato do canvas. */
function paraCanvas(definicao: DefinicaoFluxo): { nos: NoCanvas[]; arestas: Edge[] } {
  return {
    nos: definicao.nos.map((no, i) => ({
      id: no.id,
      type: 'mymaxx' as const,
      // Nós sem posição (importados ou criados fora do editor) entram em coluna.
      position: no.posicao ?? { x: 120, y: i * 160 },
      data: { no },
    })),
    arestas: definicao.arestas.map((a) => ({
      id: a.id,
      source: a.de,
      target: a.para,
      sourceHandle: a.rotulo ?? null,
      label: a.rotulo,
      animated: false,
    })),
  };
}

/** E de volta, preservando as posições que o admin arrastou. */
function paraDefinicao(nos: NoCanvas[], arestas: Edge[]): DefinicaoFluxo {
  return {
    versaoSchema: 1,
    nos: nos.map((n) => ({
      ...n.data.no,
      posicao: { x: Math.round(n.position.x), y: Math.round(n.position.y) },
    })),
    arestas: arestas.map((a): Aresta => ({
      id: a.id,
      de: a.source,
      para: a.target,
      ...(a.sourceHandle ? { rotulo: a.sourceHandle } : {}),
    })),
  };
}

export function EditorFluxo() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const permissoes = useSessao((s) => s.usuario?.permissoes ?? []);
  const podePublicar = permissoes.includes('fluxos.publicar');

  const [nos, setNos, onNosChange] = useNodesState<NoCanvas>([]);
  const [arestas, setArestas, onArestasChange] = useEdgesState<Edge>([]);
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [arestaSelecionada, setArestaSelecionada] = useState<string | null>(null);
  const [noEmExecucao, setNoEmExecucao] = useState<string | null>(null);
  const [mostrarSimulador, setMostrarSimulador] = useState(false);
  const [mostrarVersoes, setMostrarVersoes] = useState(false);
  const [aviso, setAviso] = useState<{ tipo: 'sucesso' | 'erro'; texto: string } | null>(null);
  const [sujo, setSujo] = useState(false);
  const canvas = useRef<HTMLDivElement>(null);

  const fluxo = useQuery({ queryKey: ['fluxo', id], queryFn: () => fluxosApi.detalhe(id) });
  const setores = useQuery({ queryKey: ['setores'], queryFn: gestaoApi.listarSetores });
  const fluxos = useQuery({ queryKey: ['fluxos'], queryFn: fluxosApi.listar });
  const versoes = useQuery({
    queryKey: ['fluxo-versoes', id],
    queryFn: () => fluxosApi.versoes(id),
    enabled: mostrarVersoes,
  });

  // Carrega o rascunho no canvas quando o fluxo chega (e só então).
  const carregadoDe = useRef<string | null>(null);
  useEffect(() => {
    if (!fluxo.data || carregadoDe.current === fluxo.data.id) return;
    carregadoDe.current = fluxo.data.id;
    const { nos: n, arestas: a } = paraCanvas(fluxo.data.rascunho as DefinicaoFluxo);
    setNos(n);
    setArestas(a);
    setSujo(false);
  }, [fluxo.data, setNos, setArestas]);

  const definicaoAtual = useMemo(() => paraDefinicao(nos, arestas), [nos, arestas]);

  const validacao = useQuery({
    queryKey: ['fluxo-validacao', JSON.stringify(definicaoAtual)],
    queryFn: () => fluxosApi.validar(definicaoAtual),
    enabled: nos.length > 0,
  });

  const salvar = useMutation({
    mutationFn: () => fluxosApi.salvarRascunho(id, definicaoAtual),
    onSuccess: (atualizado) => {
      queryClient.setQueryData(['fluxo', id], atualizado);
      void queryClient.invalidateQueries({ queryKey: ['fluxos'] });
      setSujo(false);
      setAviso({ tipo: 'sucesso', texto: 'Rascunho salvo.' });
    },
    onError: (e) =>
      setAviso({ tipo: 'erro', texto: e instanceof ErroApi ? e.mensagem : 'Falha ao salvar' }),
  });

  const publicar = useMutation({
    mutationFn: async () => {
      await fluxosApi.salvarRascunho(id, definicaoAtual);
      return fluxosApi.publicar(id);
    },
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ['fluxo', id] });
      void queryClient.invalidateQueries({ queryKey: ['fluxos'] });
      setSujo(false);
      setAviso({ tipo: 'sucesso', texto: `Versão ${r.versao.numero} no ar.` });
    },
    onError: (e) =>
      setAviso({
        tipo: 'erro',
        texto: e instanceof ErroApi ? e.mensagem : 'Não foi possível publicar',
      }),
  });

  const restaurar = useMutation({
    mutationFn: (versaoId: string) => fluxosApi.restaurarVersao(id, versaoId),
    onSuccess: (atualizado) => {
      carregadoDe.current = null; // força recarregar o canvas
      queryClient.setQueryData(['fluxo', id], atualizado);
      setMostrarVersoes(false);
      setAviso({ tipo: 'sucesso', texto: 'Versão trazida para o rascunho. Revise e publique.' });
    },
  });

  const conectar = useCallback(
    (conexao: Connection) => {
      setArestas((atuais) => {
        // Cada saída liga a um destino só: reconectar substitui a anterior.
        const limpo = atuais.filter(
          (a) => !(a.source === conexao.source && a.sourceHandle === conexao.sourceHandle),
        );
        return addEdge(
          {
            ...conexao,
            id: `e${Date.now().toString(36)}`,
            label: conexao.sourceHandle ?? undefined,
          },
          limpo,
        );
      });
      setSujo(true);
    },
    [setArestas],
  );

  const aoSoltar = useCallback(
    (evento: React.DragEvent) => {
      evento.preventDefault();
      const tipo = evento.dataTransfer.getData('application/mymaxx-no') as TipoNo;
      if (!tipo || !canvas.current) return;
      const area = canvas.current.getBoundingClientRect();
      const novo = noPadrao(tipo, `${tipo}_${Date.now().toString(36)}`, {
        x: evento.clientX - area.left - 120,
        y: evento.clientY - area.top - 40,
      });
      setNos((atuais) => [
        ...atuais,
        { id: novo.id, type: 'mymaxx', position: novo.posicao!, data: { no: novo } },
      ]);
      setSelecionado(novo.id);
      setSujo(true);
    },
    [setNos],
  );

  const alterarNo = useCallback(
    (atualizado: No) => {
      setNos((atuais) =>
        atuais.map((n) =>
          n.id === atualizado.id ? { ...n, data: { ...n.data, no: atualizado } } : n,
        ),
      );
      // Tirar uma opção do menu deixa arestas órfãs: some com elas.
      if (atualizado.tipo === 'menu') {
        const ids = new Set(atualizado.opcoes.map((o) => o.id));
        setArestas((atuais) =>
          atuais.filter(
            (a) => a.source !== atualizado.id || (a.sourceHandle && ids.has(a.sourceHandle)),
          ),
        );
      }
      setSujo(true);
    },
    [setNos, setArestas],
  );

  const excluirNo = useCallback(
    (noId: string) => {
      setNos((atuais) => atuais.filter((n) => n.id !== noId));
      setArestas((atuais) => atuais.filter((a) => a.source !== noId && a.target !== noId));
      setSelecionado(null);
      setSujo(true);
    },
    [setNos, setArestas],
  );

  const excluirAresta = useCallback(
    (arestaId: string) => {
      setArestas((atuais) => atuais.filter((a) => a.id !== arestaId));
      setArestaSelecionada(null);
      setSujo(true);
    },
    [setArestas],
  );

  /** Arrastou a ponta da conexão para outro nó: muda o destino, sem criar uma segunda. */
  const reconectar = useCallback(
    (anterior: Edge, nova: Connection) => {
      setArestas((atuais) =>
        atuais.map((a) =>
          a.id === anterior.id
            ? { ...a, source: nova.source, target: nova.target, sourceHandle: nova.sourceHandle }
            : a,
        ),
      );
      setSujo(true);
    },
    [setArestas],
  );

  /** Cada aresta recebe o que precisa para se desenhar e se apagar sozinha. */
  const arestasComAcoes = useMemo(
    () =>
      arestas.map((a) => ({
        ...a,
        type: 'mymaxx' as const,
        selected: a.id === arestaSelecionada,
        data: { ...a.data, aoExcluir: excluirAresta },
      })),
    [arestas, arestaSelecionada, excluirAresta],
  );

  // Destaque do nó em execução durante o teste.
  const nosComEstado = useMemo(
    () =>
      nos.map((n) => ({
        ...n,
        data: { ...n.data, emExecucao: n.id === noEmExecucao },
      })),
    [nos, noEmExecucao],
  );

  const detalhe = fluxo.data;
  const noSelecionado = nos.find((n) => n.id === selecionado)?.data.no ?? null;
  const arestaEmFoco = arestas.find((a) => a.id === arestaSelecionada) ?? null;
  const problemas = validacao.data?.problemas ?? [];

  if (fluxo.isPending) return <p className="p-6 text-sm text-neutral-500">Carregando fluxo…</p>;
  if (!detalhe) return <p className="p-6 text-sm text-red-600">Fluxo não encontrado.</p>;

  return (
    <div className="-mx-4 -my-6 flex h-[calc(100vh-57px)] flex-col">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 px-4 py-2 dark:border-neutral-800">
        <div className="flex items-center gap-3">
          <Link to="/fluxos" aria-label="Voltar para a lista" className="text-neutral-500">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <p className="text-sm font-medium">{detalhe.nome}</p>
            <p className="text-xs text-neutral-500">
              {ROTULO_GATILHO[detalhe.gatilho]} ·{' '}
              {detalhe.versaoPublicada
                ? `versão ${detalhe.versaoPublicada} no ar`
                : 'nunca publicado'}
              {sujo && ' · alterações não salvas'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {problemas.length > 0 && (
            <span className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle size={14} /> {problemas.length} pendência(s)
            </span>
          )}
          {problemas.length === 0 && nos.length > 1 && (
            <span className="flex items-center gap-1 text-xs text-green-700 dark:text-green-400">
              <Check size={14} /> pronto para publicar
            </span>
          )}
          <Botao variante="secundario" onClick={() => setMostrarVersoes(true)}>
            <History size={15} /> Versões
          </Botao>
          <Botao variante="secundario" onClick={() => setMostrarSimulador((v) => !v)}>
            <Play size={15} /> Testar
          </Botao>
          <Botao
            variante="secundario"
            onClick={() => salvar.mutate()}
            carregando={salvar.isPending}
          >
            <Save size={15} /> Salvar
          </Botao>
          {podePublicar && (
            <Botao
              onClick={() => publicar.mutate()}
              carregando={publicar.isPending}
              disabled={problemas.length > 0}
              title={problemas.length > 0 ? 'Resolva as pendências antes de publicar' : undefined}
            >
              <Upload size={15} /> Publicar
            </Botao>
          )}
        </div>
      </header>

      {aviso && (
        <div className="px-4 pt-2">
          <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {/* Paleta */}
        <div className="w-52 shrink-0 space-y-1 overflow-y-auto border-r border-neutral-200 p-3 dark:border-neutral-800">
          <p className="mb-2 text-xs font-medium text-neutral-500 uppercase">
            Arraste para o canvas
          </p>
          {NOS_DA_PALETA.map((n) => (
            <div
              key={n.tipo}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('application/mymaxx-no', n.tipo);
                e.dataTransfer.effectAllowed = 'move';
              }}
              title={n.descricao}
              className="flex cursor-grab items-center gap-2 rounded-lg border border-neutral-200 px-2 py-1.5 text-xs transition hover:border-marca-400 active:cursor-grabbing dark:border-neutral-700"
            >
              <n.Icone size={14} style={{ color: n.cor }} />
              {n.nome}
            </div>
          ))}

          {problemas.length > 0 && (
            <div className="mt-4 rounded-lg bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
              <p className="mb-1 font-medium">Antes de publicar</p>
              <ul className="list-inside list-disc space-y-0.5">
                {problemas.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Canvas */}
        <div
          ref={canvas}
          className="min-w-0 flex-1"
          onDrop={aoSoltar}
          onDragOver={(e) => e.preventDefault()}
        >
          <ReactFlow
            nodes={nosComEstado}
            edges={arestasComAcoes}
            nodeTypes={TIPOS_DE_NO}
            edgeTypes={TIPOS_DE_ARESTA}
            onNodesChange={(mudancas: NodeChange<NoCanvas>[]) => {
              onNosChange(mudancas);
              if (mudancas.some((m) => m.type === 'position' && m.dragging === false))
                setSujo(true);
            }}
            onEdgesChange={(mudancas) => {
              onArestasChange(mudancas);
              if (mudancas.some((m) => m.type === 'remove')) setSujo(true);
            }}
            onConnect={conectar}
            // Arrastar a ponta de uma conexão para outro nó move a ligação em vez de duplicá-la.
            onReconnect={reconectar}
            reconnectRadius={18}
            onNodeClick={(_, no) => {
              setSelecionado(no.id);
              setArestaSelecionada(null);
            }}
            onEdgeClick={(_, aresta) => {
              setArestaSelecionada(aresta.id);
              setSelecionado(null);
            }}
            onPaneClick={() => {
              setSelecionado(null);
              setArestaSelecionada(null);
            }}
            // Filtra o que vai ser apagado ANTES da remoção: o início é obrigatório e fica.
            onBeforeDelete={({ nodes, edges }) => {
              const permitidos = nodes.filter((n) => n.data.no.tipo !== 'inicio');
              if (permitidos.length !== nodes.length) {
                setAviso({ tipo: 'erro', texto: 'O nó de início não pode ser excluído.' });
              }
              // O React Flow inclui as conexões do nó; as do início só saem se o próprio
              // usuário as tiver selecionado.
              const idsApagados = new Set(permitidos.map((n) => n.id));
              const arestas = edges.filter(
                (e) => e.selected || idsApagados.has(e.source) || idsApagados.has(e.target),
              );
              if (permitidos.length === 0 && arestas.length === 0) return Promise.resolve(false);
              return Promise.resolve({ nodes: permitidos, edges: arestas });
            }}
            onNodesDelete={() => {
              setSelecionado(null);
              setSujo(true);
            }}
            onEdgesDelete={() => setSujo(true)}
            fitView
            proOptions={{ hideAttribution: true }}
            // Delete e Backspace apagam o que estiver selecionado — nó ou conexão.
            deleteKeyCode={['Delete', 'Backspace']}
            // Controles e minimapa acompanham o tema claro/escuro do painel.
            colorMode="system"
          >
            <Background />
            <Controls />
            {/* Como mexer no canvas, à vista — ninguém deveria precisar adivinhar. */}
            <Panel position="bottom-center">
              <p className="rounded-full border border-neutral-200 bg-white/90 px-3 py-1 text-[11px] text-neutral-500 shadow-sm backdrop-blur dark:border-neutral-700 dark:bg-neutral-900/90">
                Arraste da bolinha de saída até outro nó para ligar · clique na conexão e use o ×
                (ou Delete) para apagar · arraste a ponta para mudar o destino
              </p>
            </Panel>
            <MiniMap
              pannable
              zoomable
              nodeColor={(n) => CATALOGO_NOS[(n as NoCanvas).data.no.tipo].cor}
            />
          </ReactFlow>
        </div>

        {noSelecionado && !mostrarSimulador && (
          <PropriedadesNo
            no={noSelecionado}
            setores={setores.data ?? []}
            fluxos={(fluxos.data ?? []).filter((f) => f.id !== id)}
            onChange={alterarNo}
            onExcluir={() => excluirNo(noSelecionado.id)}
          />
        )}

        {arestaEmFoco && !noSelecionado && !mostrarSimulador && (
          <PropriedadesAresta
            de={nos.find((n) => n.id === arestaEmFoco.source)?.data.no}
            para={nos.find((n) => n.id === arestaEmFoco.target)?.data.no}
            rotulo={arestaEmFoco.sourceHandle ?? undefined}
            onExcluir={() => excluirAresta(arestaEmFoco.id)}
          />
        )}

        {mostrarSimulador && (
          <Simulador
            fluxoId={id}
            temRascunhoNaoPublicado={detalhe.temAlteracoesNaoPublicadas}
            temVersaoPublicada={detalhe.versaoPublicada !== null}
            onNoAtual={setNoEmExecucao}
            onFechar={() => {
              setMostrarSimulador(false);
              setNoEmExecucao(null);
            }}
          />
        )}
      </div>

      <Modal
        aberto={mostrarVersoes}
        titulo="Versões publicadas"
        descricao="Restaurar traz a versão escolhida para o rascunho; nada vai ao ar até você publicar."
        onFechar={() => setMostrarVersoes(false)}
        acoes={<Botao onClick={() => setMostrarVersoes(false)}>Fechar</Botao>}
      >
        <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {versoes.data?.map((v: VersaoFluxo) => (
            <li key={v.id} className="flex items-center justify-between gap-3 py-2">
              <div>
                <p className="text-sm font-medium">
                  Versão {v.numero}
                  {v.noAr && (
                    <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800 dark:bg-green-950 dark:text-green-200">
                      no ar
                    </span>
                  )}
                </p>
                <p className="text-xs text-neutral-500">
                  {new Date(v.publicadaEm).toLocaleString('pt-BR')}
                  {v.publicadaPor && ` · ${v.publicadaPor}`}
                </p>
              </div>
              <Botao
                variante="secundario"
                onClick={() => restaurar.mutate(v.id)}
                carregando={restaurar.isPending}
              >
                Restaurar
              </Botao>
            </li>
          ))}
          {versoes.data?.length === 0 && (
            <li className="py-4 text-center text-sm text-neutral-500">
              Este fluxo ainda não foi publicado.
            </li>
          )}
        </ul>
      </Modal>
    </div>
  );
}

/** Reexport para a rota (mantém o import da página curto). */
export type { FluxoDetalhe };
